import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ServicePageTemplate from "@/components/ServicePageTemplate";
import SEO, { serviceJsonLd } from "@/components/SEO";
import { useT } from "@/lib/i18n";
import { IMG } from "@/lib/config";

// Map i18nKey → public URL path + SEO service type
const SERVICE_PATHS = {
  codir: { path: "/dirigeants-codir", type: "Executive Coaching" },
  management: { path: "/management-leadership", type: "Management Development" },
  ecole: { path: "/ecole-management", type: "Management Academy" },
  coo: { path: "/coo-conseil", type: "Operational Advisory" },
  seminaires: { path: "/seminaires", type: "Corporate Seminar" },
  pcm: { path: "/pcm", type: "PCM Coaching" },
  rise: { path: "/rise", type: "Executive Immersion" },
};

/**
 * Generic adapter that renders a ServicePageTemplate from a translation key.
 * Each service page just declares its i18n root + image + optional decorations.
 */
export default function I18nServicePage({
  i18nKey,
  image,
  testIdPrefix,
  accentClass = "text-accent-pink",
  badge,
  extraCta,
  afterSections,
}) {
  const t = useT();
  const root = `services.${i18nKey}`;
  const titleA = t(`${root}.titleA`, "");
  const titleAccent = t(`${root}.titleAccent`, "");
  const titleB = t(`${root}.titleB`, "");

  const sections = t(`${root}.sections`, []) || [];
  const bullets = t(`${root}.bullets`, []) || [];
  const formats = t(`${root}.formats`, []) || [];

  // SEO
  const seoMap = SERVICE_PATHS[i18nKey];
  const seoTitle = `${titleA}${titleAccent}${titleB}`.replace(/\.$/, "");
  const seoDesc = t(`${root}.lede`);
  const jsonLd = seoMap
    ? serviceJsonLd({
        name: `${t(`${root}.eyebrow`)} — WeDo`,
        description: seoDesc,
        path: seoMap.path,
        serviceType: seoMap.type,
      })
    : null;

  return (
    <>
      {seoMap && (
        <SEO
          title={seoTitle}
          description={seoDesc}
          path={seoMap.path}
          jsonLd={jsonLd}
        />
      )}
      <ServicePageTemplate
        testIdPrefix={testIdPrefix}
        eyebrow={t(`${root}.eyebrow`)}
        title={
          <>
            {titleA}
            <em className={`not-italic ${accentClass}`}>{titleAccent}</em>
            {titleB}
          </>
        }
        lede={t(`${root}.lede`)}
        image={image}
        badge={
          badge
            ? { src: badge.src, alt: t(`${root}.badgeAlt`, badge.alt || "") }
            : undefined
        }
        extraCta={extraCta}
        afterSections={afterSections}
        sections={Array.isArray(sections) ? sections : []}
        bullets={Array.isArray(bullets) ? bullets : []}
        formats={Array.isArray(formats) ? formats : []}
      />
    </>
  );
}

/** Reusable RISE CTA button (uses translation). */
export function RiseInfoCta() {
  const t = useT();
  return (
    <Link
      to="/contact?subject=Demande%20d%27information%20RISE"
      data-testid="rise-info-cta"
      className="btn-pill bg-accent-pink text-white hover:bg-white hover:text-brand"
    >
      {t("common.cta.requestInfo")} <ArrowRight size={16} />
    </Link>
  );
}
